// deps
import React, { memo, useCallback, useRef, useState } from "react";

// constants
import {
    CANCEL_BOOKING_CTA_ANALYTICS_FLAGS,
    CANCEL_BOOKING_TEXTS,
    OOPS_SOMETHING_WENT_WRONG,
} from "@dineout-payment-success-ui/constants/Constants";

// internal-deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Popup } from "@swiggy-private/dls-web-core/dist/components/Popup";

// styles
import { CancelBookingCTA, CancelBookingCTAWrapper } from "./styles";
import CancelBookingPopup from "./CancelBookingPopup";
import { cancelBookingWrapper } from "@dineout-payment-success-ui/apis/BookingSuccess";

// interfaces
import {
    IClickCancelBookingCta,
    IClickCancelBookingPopupCta,
} from "@dineout-payment-success-ui/interfaces/AnalyticsEvents";

interface ICancelBookingWidgetProps {
    ctaText?: string;
    leftText?: string;
    bookingId?: string;
    isCheckoutFlow: boolean;
    dealCategory: string;
    reloadSuccessPage: VoidFunction;
    setSoftError: (error: string) => void;
    analyticsEvents: {
        onClickCancelBookingPopupCta: (isCheckoutFlow: boolean, props: IClickCancelBookingPopupCta) => void;
        onClickCancelBookingCta: (isCheckoutFlow: boolean, props: IClickCancelBookingCta) => void;
    };
}

const CancelBookingWidget: React.FC<ICancelBookingWidgetProps> = ({
    leftText,
    ctaText,
    bookingId,
    reloadSuccessPage,
    setSoftError,
    analyticsEvents,
    isCheckoutFlow,
    dealCategory,
}) => {
    const cancelApiCallRef = useRef(false);
    const [loading, setLoading] = useState(false);

    const [showCancelBookingPopup, setShowCancelBookingPopup] = useState(false);

    const handleCloseCancelBookingPopup = useCallback(() => {
        analyticsEvents?.onClickCancelBookingPopupCta(isCheckoutFlow, {
            orderId: bookingId || "",
            dealCategory,
            action: CANCEL_BOOKING_CTA_ANALYTICS_FLAGS.DONE_CANCEL_BOOKING,
        });
        setShowCancelBookingPopup(false);
    }, [analyticsEvents, bookingId, dealCategory, isCheckoutFlow]);

    const handleOpenCancelBookingPopup = useCallback(() => {
        analyticsEvents?.onClickCancelBookingCta(isCheckoutFlow, {
            orderId: bookingId || "",
            dealCategory,
        });
        setShowCancelBookingPopup(true);
    }, [analyticsEvents, bookingId, dealCategory, isCheckoutFlow]);

    const handleCancelBooking = useCallback(async () => {
        try {
            if (cancelApiCallRef.current) return;

            setLoading(true);
            cancelApiCallRef.current = true;
            const cancelBookingResp = await cancelBookingWrapper(bookingId || "");
            analyticsEvents?.onClickCancelBookingPopupCta(isCheckoutFlow, {
                orderId: bookingId || "",
                dealCategory,
                action: CANCEL_BOOKING_CTA_ANALYTICS_FLAGS.CANCEL_BOOKING,
            });

            if (cancelBookingResp.isErr()) {
                throw cancelBookingResp;
            }
            setLoading(false);
            cancelApiCallRef.current = false;
            reloadSuccessPage();
        } catch (error) {
            setLoading(false);
            cancelApiCallRef.current = false;
            setSoftError(OOPS_SOMETHING_WENT_WRONG);
            handleCloseCancelBookingPopup();
        }
    }, [
        analyticsEvents,
        bookingId,
        dealCategory,
        handleCloseCancelBookingPopup,
        isCheckoutFlow,
        reloadSuccessPage,
        setSoftError,
    ]);

    return (
        <>
            <CancelBookingCTAWrapper>
                <Text variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                    {leftText || CANCEL_BOOKING_TEXTS.MAIN_TEXT}
                </Text>
                <CancelBookingCTA buttonSize="small" color="primary" onClick={handleOpenCancelBookingPopup}>
                    {ctaText || CANCEL_BOOKING_TEXTS.BUTTON_TEXT}
                </CancelBookingCTA>
            </CancelBookingCTAWrapper>
            <Popup
                isOpen={showCancelBookingPopup}
                closeOnOverlayClick
                onClose={handleCloseCancelBookingPopup}
                width={CANCEL_BOOKING_TEXTS.POPUP_WIDTH}
            >
                <CancelBookingPopup
                    onCancelBooking={handleCancelBooking}
                    onClose={handleCloseCancelBookingPopup}
                    loading={loading}
                />
            </Popup>
        </>
    );
};

export default memo(CancelBookingWidget);
